/* RESPONSIBLE TEAM: team-reporting */
import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import generateUUID from 'embercom/lib/uuid-generator';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import type Chart from 'embercom/models/reporting/custom/chart';

export interface ReportingDrillInBridgeOpenPayload {
  chartDefinition?: Record<string, unknown>;
  columns?: string[];
  eventPlace?: string;
  isStandalone?: boolean;
  views?: string[];
  // For multi-metric custom report drill-in: which series the user picked,
  // identified by index into chartDefinition.chartSeries. Index (not
  // metricId) because the same metric can appear in multiple series with
  // different aggregations or labels — a metricId match would always
  // resolve to the first occurrence. Falls back to firstObject when
  // omitted or out of range.
  selectedSeriesIndex?: number;
}

export default class ReportingDrillInBridgeService extends Service {
  @service declare store: Store;
  @service declare reportingMetrics: ReportingMetrics;

  @tracked isOpen = false;
  @tracked payload: ReportingDrillInBridgeOpenPayload | null = null;
  @tracked chartRecord: Chart | null = null;

  async open(payload: ReportingDrillInBridgeOpenPayload) {
    this.payload = { ...payload };

    let def = payload?.chartDefinition as Record<string, unknown>;
    let incomingId = def?.['id'];
    let hasIncomingId = typeof incomingId === 'string' && incomingId.length > 0;

    // Prefer the existing record in the store. The custom-report flow
    // already loaded the chart with its full per-series filters / color /
    // percentileValue, but the React-side ChartConfig is a stripped
    // projection that omits those fields — re-pushing would silently
    // overwrite them, breaking drill-in for charts with metric-level
    // filters. Only push when the record isn't already in the store
    // (e.g. AI categories / fin-guidance flows that synthesize their own
    // ids and have no live chart record to merge with).
    //
    // ID-namespace convention (load-bearing): real custom-chart records
    // use numeric ids from the Rails serializer. Synthetic callers MUST
    // use a prefixed string id that cannot collide — e.g.
    // `ai-categories-drill-${id}`, `fin-guidance-drill-${id}`. A
    // collision routes a synthetic-payload caller into the peek branch
    // and reuses the wrong saved record.
    let existing = hasIncomingId
      ? (this.store.peekRecord('reporting/custom/chart', incomingId as string) as Chart | null)
      : null;

    if (existing) {
      this.chartRecord = existing;
    } else {
      let chartId: string = hasIncomingId ? (incomingId as string) : generateUUID();
      let sanitized = { ...def, id: chartId };
      this.store.pushPayload({ 'reporting/custom/chart': sanitized });
      this.chartRecord = this.store.peekRecord('reporting/custom/chart', chartId) as Chart;
    }

    this.isOpen = true;
  }

  close() {
    this.isOpen = false;
    this.payload = null;
    this.chartRecord = null;
  }
}

declare module '@ember/service' {
  interface Registry {
    'reporting-drill-in-bridge': ReportingDrillInBridgeService;
    reportingDrillInBridge: ReportingDrillInBridgeService;
  }
}
